import { coin, downArrow, logo, speaker, wallet } from "@/constants";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NavLink, useNavigate } from "react-router-dom";
import LoginDialog from "@/pages/Login/LoginDialog";
import WalletDialog from "@/pages/Wallet/Wallet";
import useUserStore from "@/stores/authentication/store";
import ProfileDropdown from "./ProfileDropdown";
import React, { useState } from "react";
import SidebarMobile from "./SidebarMobile";
import NotificationDropdownV2 from "@/components/notifications/notificationDropDown.tsx";

import { useMute } from "@/providers/audioProvider";
import useBalanceStore from "@/stores/user/balance.store";
import {
  CasinoIcon,
  TennisIcon,
  CrownIcon,
  SoundIcon,
  ChatIcon,
  Hamburger,
} from "@/assets/icons/ocean/icons";
import ChatBar from "./ChatBar";
import { getCdnUrl } from "@/lib/utils";

interface NavbarProps {
  isBottomBarVisible: boolean;
}

const Navbar = ({ isBottomBarVisible }: NavbarProps) => {
  const { isMuted, toggleMute } = useMute();
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  const { isAuthenticated, data } = useUserStore();
  const { balance, bonusBalance, nativeBalance } = useBalanceStore();

  const userData = data;
  const profilePicture = userData?.profilePicture;
  const profilePictureFallback = userData?.name?.charAt(0).toUpperCase();
  const userName = userData?.name;

  const [isSidebarMobileOpen, setIsSidebarMobileOpen] =
    useState<boolean>(false);

  return (
    <>
      <div className="sticky top-0 z-50 flex min-h-[55px] items-center justify-between bg-primary sm:min-h-[80px] sm:p-3 md:h-[84px]">
        {/* left side */}
        <div className="flex w-full flex-row items-center justify-between px-2">
          {/* logo */}
          <NavLink to="/">
            <img
              src={getCdnUrl("assets/logo.svg")}
              alt="logo"
              className="-mt-3 h-10 lg:h-[4rem]"
            />
          </NavLink>

          <div className="hidden flex-row gap-2 xl:flex">
            <button
              className="text-md flex flex-row items-center gap-2 rounded-sm bg-button-gradient px-4 py-2 text-white"
              onClick={() => navigate("/casino/lobby")}
            >
              <CasinoIcon />
              <div>Casino</div>
            </button>

            <button className="disabled text-md flex flex-row items-center gap-2 rounded-sm bg-button-gradient px-4 py-2 text-white opacity-20">
              <TennisIcon />
              <div>Sports</div>
            </button>

            <button className="text-md flex flex-row items-center gap-2 rounded-sm bg-elite-gradient px-4 py-2 text-white">
              <CrownIcon />
              <div onClick={() => navigate("/elite")}>Elite Gamers Club</div>
            </button>
          </div>

          {/* right side */}
          <div className="flex items-center gap-2 sm:gap-4">
            {isAuthenticated && (
              <div className="hidden gap-4 sm:flex">
                <Button className="h-[46px] bg-button-gradient text-[15px] leading-[18px] text-white">
                  <img
                    src={getCdnUrl("assets/icons/profile/sharkCoin100.svg")}
                    className="mr-2 h-[25px] w-[25px]"
                  />
                  {nativeBalance?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Button>
                <Button className="h-[46px] bg-button-gradient text-[15px] leading-[18px] text-white">
                  <img
                    src={coin as string}
                    alt="coin"
                    className="mr-2 w-5 sm:w-6"
                  />
                  {balance?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  {bonusBalance > 0 && (
                    <span className="ml-1 text-success">
                      (+
                      {bonusBalance?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      )
                    </span>
                  )}
                </Button>
                <WalletDialog
                  initialActiveButton="deposit"
                  trigger={
                    <Button className="h-[46px] bg-button-gradient text-[15px] leading-[18px] text-white">
                      <img
                        src={wallet as string}
                        alt="wallet"
                        className="mr-2 w-5 sm:w-6"
                      />
                      Wallet
                    </Button>
                  }
                />
              </div>
            )}

            {isAuthenticated && (
              <>
                <Button
                  className="size-8 sm:hidden sm:size-[46px]"
                  onClick={toggleMute}
                  style={{
                    opacity: isMuted ? 0.5 : 1,
                  }}
                >
                  <img
                    src={speaker as string}
                    alt="alerts"
                    className="min-w-5 sm:min-w-6"
                  />
                </Button>
              </>
            )}

            <div className="hidden flex-row gap-2 lg:flex">
              <NotificationDropdownV2></NotificationDropdownV2>
              <Button
                className="h-[44px] w-[44px] bg-primary-background p-0"
                style={{
                  opacity: isMuted ? 0.5 : 1,
                }}
                onClick={toggleMute}
              >
                <SoundIcon />
              </Button>
              <Button
                className="h-[44px] w-[44px] bg-primary-background p-0"
                onClick={() => setIsChatOpen(!isChatOpen)}
              >
                <ChatIcon />
              </Button>
            </div>

            <ChatBar
              isOpen={isChatOpen}
              handleClose={() => setIsChatOpen(false)}
            />

            {isAuthenticated ? (
              <>
                <ProfileDropdown
                  trigger={
                    <Avatar className="hidden size-12 cursor-pointer rounded-full border border-[#2998FF] p-1 sm:flex">
                      <AvatarImage
                        src={profilePicture}
                        className="h-full w-full rounded-full"
                      />
                      <AvatarFallback className="text-xs">
                        {profilePictureFallback}
                      </AvatarFallback>
                    </Avatar>
                  }
                />

                {/* Profile dropdown - mobile */}
                <ProfileDropdown
                  trigger={
                    <Button className="flex h-8 px-2 sm:hidden">
                      <Avatar className="mr-1.5 size-[21px] rounded-full border border-[#2998FF] p-1">
                        <AvatarImage
                          src={profilePicture}
                          className="h-full w-full rounded-full"
                        />
                        <AvatarFallback className="text-xs">
                          {profilePictureFallback}
                        </AvatarFallback>
                      </Avatar>
                      <span className="mr-1.5 font-kanit text-[12px] font-medium leading-[18px] text-white">
                        {userName}
                      </span>
                      <img
                        src={downArrow as string}
                        alt="arrow"
                        className="min-w-3"
                      />
                    </Button>
                  }
                />
              </>
            ) : (
              <>
                <LoginDialog
                  initialActiveButton="signup"
                  trigger={
                    <Button className="h-[28px] rounded-sm border-2 border-primary-text text-xs text-primary-text md:h-[44px] md:text-[15px]">
                      Sign Up
                    </Button>
                  }
                />

                <LoginDialog
                  initialActiveButton="login"
                  trigger={
                    <Button className="h-[28px] rounded-sm bg-button-gradient text-xs text-white md:h-[44px] md:text-[15px]">
                      Login
                    </Button>
                  }
                />

                {isAuthenticated && (
                  <ProfileDropdown
                    trigger={
                      <img
                        src={downArrow as string}
                        alt="arrow"
                        className="mr-1 w-[14px] cursor-pointer sm:hidden sm:w-6"
                      />
                    }
                  />
                )}
              </>
            )}

            {/* sidebar mobile button */}
            <Button
              className="flex items-center lg:hidden"
              onClick={() => setIsSidebarMobileOpen(!isSidebarMobileOpen)}
            >
              <Hamburger />
            </Button>
          </div>
        </div>
      </div>
      <SidebarMobile
        isOpen={isSidebarMobileOpen}
        handleClose={() => setIsSidebarMobileOpen(!isSidebarMobileOpen)}
        isBottomBarVisible={isBottomBarVisible}
      />
    </>
  );
};

export default Navbar;
